import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { useUser } from "../../contexts/UserContext";
import { useEffect, useState } from "react";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";

const plans = [
  {
    id: "price_1QMF5eGPImEcGaYISqqXfzGh",
    name: "Auction Records",
    description: "Access to comprehensive auction price database",
    price: 250,
    unit: "per 100 requests",
    features: [
      "Search 1M+ auction records",
      "Filter by artist, medium, date",
      "Price history and trends",
      "Export to CSV",
      "API access included",
    ],
    popular: false,
  },
  {
    id: "price_1QUx3SGPImEcGaYItaLKVKHL",
    name: "AI Valuation",
    description: "AI-powered artwork valuation and forecasting",
    price: 250,
    unit: "per 50 requests",
    features: [
      "AI-powered valuations",
      "Market trend analysis",
      "Price forecasting",
      "Comparable sales data",
      "Detailed PDF reports",
      "Priority support",
    ],
    popular: true,
  },
];

const SubscriptionsPage: React.FC = () => {
  const { user: authUser } = useAuth();
  const { user } = useUser();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  useEffect(() => {
    if (!authUser?.uid) return;

    const docRef = query(
      collection(db, "subscriptions"),
      where("userId", "==", authUser.uid)
    );

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSubscriptions(items);
      },
      (err) => {
        console.error(err);
      }
    );

    return () => unsubscribe();
  }, [authUser?.uid]);

  const handleSubscribe = async (priceId: string) => {
    setProcessingPlan(priceId);
    try {
      const generateCheckout = httpsCallable(functions, "generateCheckoutSession");
      const { data } = (await generateCheckout({
        priceId,
        redirectUrl: window.origin + "/subscriptions",
      })) as { data: { url: string } };
      window.open(data.url, "_blank");
    } catch (error) {
      console.error("Error creating checkout session:", error);
      alert("Failed to start checkout. Please try again.");
    } finally {
      setProcessingPlan(null);
    }
  };

  const subscribedPlanIds = subscriptions.map((s) => s.plan?.id);

  return (
    <div className="min-h-screen bg-gallery-50">
      {/* Header */}
      <div className="bg-white border-b border-gallery-200">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-3">
            Pricing
          </p>
          <h1 className="font-display text-display-md text-gallery-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg text-gallery-600 max-w-2xl mx-auto">
            Access professional-grade art market analytics and AI-powered valuations
            trusted by collectors, galleries, and institutions worldwide.
          </p>

          {/* Free Credits Badge */}
          {user?.freeCredits !== undefined && user.freeCredits > 0 && (
            <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-gallery-100 rounded-full">
              <span className="w-2 h-2 bg-emerald-500 rounded-full" />
              <span className="text-sm text-gallery-700">
                You have <span className="font-semibold">{user.freeCredits} free credits</span> remaining
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Plans */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {plans.map((plan) => {
            const isSubscribed = subscribedPlanIds.includes(plan.id);
            const isProcessing = processingPlan === plan.id;

            return (
              <div
                key={plan.id}
                className={`relative bg-white border rounded-sm overflow-hidden ${
                  plan.popular
                    ? "border-gallery-900 ring-1 ring-gallery-900"
                    : "border-gallery-200"
                }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gallery-900 text-white text-xs font-medium px-3 py-1">
                    Most Popular
                  </div>
                )}

                <div className="p-8">
                  {/* Plan Header */}
                  <h2 className="font-display text-2xl text-gallery-900 mb-2">
                    {plan.name}
                  </h2>
                  <p className="text-sm text-gallery-500 mb-6">{plan.description}</p>

                  {/* Price */}
                  <div className="mb-8">
                    <span className="font-display text-4xl text-gallery-900">
                      ${plan.price}
                    </span>
                    <span className="text-gallery-500 ml-2">{plan.unit}</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <svg
                          className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="text-sm text-gallery-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  {isSubscribed ? (
                    <div className="w-full py-3 px-4 bg-emerald-50 text-emerald-700 text-sm font-medium text-center rounded-sm border border-emerald-200">
                      <span className="inline-flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Currently Subscribed
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={isProcessing}
                      className={`w-full py-3 px-4 text-sm font-medium rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        plan.popular
                          ? "bg-gallery-900 text-white hover:bg-gallery-800"
                          : "bg-white text-gallery-900 border border-gallery-300 hover:bg-gallery-50"
                      }`}
                    >
                      {isProcessing ? (
                        <span className="inline-flex items-center gap-2">
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Processing...
                        </span>
                      ) : (
                        "Subscribe Now"
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Enterprise Section */}
        <div className="mt-12 bg-gallery-900 rounded-sm p-8 text-center">
          <h3 className="font-display text-2xl text-white mb-2">
            Enterprise Solutions
          </h3>
          <p className="text-gallery-300 mb-6 max-w-2xl mx-auto">
            Need custom integrations, higher volume, or dedicated support?
            Our enterprise plans are tailored for institutions, auction houses, and large galleries.
          </p>
          <a
            href="mailto:enterprise@artcollection.io"
            className="inline-flex items-center px-6 py-3 bg-white text-gallery-900 text-sm font-medium rounded-sm hover:bg-gallery-100 transition-colors"
          >
            Contact Sales
            <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="font-display text-xl text-gallery-900 mb-8 text-center">
            Frequently Asked Questions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gallery-200 rounded-sm p-6">
              <h4 className="font-medium text-gallery-900 mb-2">
                What happens when I run out of credits?
              </h4>
              <p className="text-sm text-gallery-600">
                You can purchase additional credits at any time. Your subscription will
                automatically renew, or you can upgrade to a higher tier for better rates.
              </p>
            </div>
            <div className="bg-white border border-gallery-200 rounded-sm p-6">
              <h4 className="font-medium text-gallery-900 mb-2">
                Can I cancel my subscription?
              </h4>
              <p className="text-sm text-gallery-600">
                Yes, you can cancel anytime from the billing portal. Your access will
                continue until the end of your current billing period.
              </p>
            </div>
            <div className="bg-white border border-gallery-200 rounded-sm p-6">
              <h4 className="font-medium text-gallery-900 mb-2">
                Do unused credits roll over?
              </h4>
              <p className="text-sm text-gallery-600">
                Yes, unused credits roll over to the next billing period as long as
                your subscription remains active.
              </p>
            </div>
            <div className="bg-white border border-gallery-200 rounded-sm p-6">
              <h4 className="font-medium text-gallery-900 mb-2">
                Is there API access included?
              </h4>
              <p className="text-sm text-gallery-600">
                Yes, all plans include full API access. You can find your API keys
                and documentation in the Developer section of your dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionsPage;
